/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * SyllabusUploadCore.jsx
 * Main landing component to handle syllabus upload UI
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import { UploadSyllabus } from '@study/components';

import Icons from '../icons/Icon';
import FooterDeatilsCard from '../common/FooterDeatilsCard';
import UploadSyllabusWrapper from './styles/SyllabusUploadCoreStyles';

export default function SyllabusUploadCore({
  textInfo,
  useChannelsComponent,
  clientInformation,
  env,
  locale,
  channelId,
  textbook,
  tocData,
  locationInApp,
  onStateChange,
  onScreenChange,
  onOfflineAction,
  getUserCallback,
  syllabusStyles,
  onUploadFileName,
  handleUploadingScreenChange,
  className,
  onSyllabusExtracted,
  onFileError,
  showSyllabusUploadTabs
}) {
  const [isFooterDialogOpen, setFooterDialogOpen] = useState(false);

  const openFooterDialog = () => setFooterDialogOpen(true);
  const closeFooterDialog = () => setFooterDialogOpen(false);

  const handleUploadComplete = (syllbsData) => {
    onSyllabusExtracted?.({
      id: '',
      uploadUrl: '',
      syllabusInfo: syllbsData
    });
  };

  const handleonScreenChange = (...rest) => {
    onScreenChange?.(...rest);
    handleUploadingScreenChange?.(true);
  };

  let content;

  if (useChannelsComponent) {
    content = (
      <UploadSyllabusWrapper>
        <UploadSyllabus
          env={env}
          locale={locale}
          channelId={channelId}
          textbook={textbook}
          chapters={tocData}
          locationInApp={locationInApp}
          onComplete={handleUploadComplete}
          onStateChange={onStateChange}
          onScreenChange={handleonScreenChange}
          onOfflineAction={onOfflineAction}
          open
          dialog={false}
          syllabusAPI
          overrideUserToken={clientInformation?.headers?.authorization}
          tenantId={clientInformation?.headers?.['x-tenant-id']}
          getUserCallback={getUserCallback}
          styles={{
            ...syllabusStyles,
            header: { display: 'none' },
            uploadSyllabus: {
              ...syllabusStyles?.uploadSyllabus,
              dropZoneIcon: {
                boxSizing: 'content-box'
              },
              subtitle: {
                textAlign: 'left'
              },
              uploadButton: {
                padding: '8px 24px',
                alignSelf: 'flex-end'
              }
            }
          }}
          config={{
            uploadTitleText: textInfo?.uploadTitleText,
            fileDropInstructionText: textInfo?.uploadInputLabel,
            aiSubtitleText: !showSyllabusUploadTabs && textInfo?.aiSubtitleText ? (
              <>
                <Typography className={className.footer}>
                  {textInfo.aiSubtitleText}

                  {textInfo.aiSubtitleTextfooterDialogDetails && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFooterDialog();
                    }}
                  >
                    <Icons name="FooterInfoIcon" style={{ fontSize: 16 }} />
                  </IconButton>
                  )}
                </Typography>
              </>
            ) : null,
            ProcessingScreen: {
              ProcessingText: textInfo?.syllabusProcessingText,
              BulletPoints: textInfo?.syllabusBulletPoints
            },
            uploadErrorMessage: textInfo.syllabusUploadErrorMessage
          }}
          onUploadSuccess={onUploadFileName}
          onError={(err) => { onFileError?.(err); }}
        />
      </UploadSyllabusWrapper>
    );
  }

  return (
    <Box>
      {content}
      {textInfo?.aiSubtitleTextfooterDialogDetails && (
        <FooterDeatilsCard
          open={isFooterDialogOpen}
          onClose={closeFooterDialog}
          title={textInfo.aiSubtitleTextfooterDialogDetails.title}
          content={textInfo.aiSubtitleTextfooterDialogDetails.content}
          confirmText={textInfo.aiSubtitleTextfooterDialogDetails.confirmText}
          showCloseButton={
            textInfo.aiSubtitleTextfooterDialogDetails.showCloseButton ?? true
          }
          customStyles={{
            '--disclaimer-max-width': '720px'
          }}
          onConfirm={
            textInfo.aiSubtitleTextfooterDialogDetails.onConfirm || closeFooterDialog
          }
        />
      )}
    </Box>
  );
}

SyllabusUploadCore.propTypes = {
  useChannelsComponent: PropTypes.bool,
  clientInformation: PropTypes.object,
  textInfo: PropTypes.shape({
    uploadTitleText: PropTypes.string,
    uploadDesc: PropTypes.string,
    progressTitle: PropTypes.string,
    progressDesc: PropTypes.string,
    uploadInputLabel: PropTypes.string,
    aiSubtitleText: PropTypes.string,
    syllabusProcessingText: PropTypes.string,
    syllabusBulletPoints: PropTypes.array,
    syllabusUploadErrorMessage: PropTypes.string,
    aiSubtitleTextfooterDialogDetails: PropTypes.object
  }),
  showSyllabusUploadTabs: PropTypes.bool,
  env: PropTypes.string,
  locale: PropTypes.string,
  channelId: PropTypes.string,
  textbook: PropTypes.object,
  tocData: PropTypes.array,
  locationInApp: PropTypes.string,
  onStateChange: PropTypes.func,
  onScreenChange: PropTypes.func,
  onOfflineAction: PropTypes.func,
  getUserCallback: PropTypes.func,
  syllabusStyles: PropTypes.object,
  onUploadFileName: PropTypes.func,
  handleUploadingScreenChange: PropTypes.func,
  className: PropTypes.object,
  onSyllabusExtracted: PropTypes.func,
  onFileError: PropTypes.func
};

SyllabusUploadCore.defaultProps = {
  textInfo: {},
  useChannelsComponent: false,
  clientInformation: {},
  env: 'stage',
  locale: 'en',
  channelId: '',
  textbook: {},
  tocData: [],
  locationInApp: '',
  onStateChange: () => {},
  onScreenChange: () => {},
  onOfflineAction: () => {},
  getUserCallback: () => {},
  syllabusStyles: {},
  onUploadFileName: () => {},
  handleUploadingScreenChange: () => {},
  showSyllabusUploadTabs: false,
  className: {},
  onSyllabusExtracted: () => {},
  onFileError: () => {}
};
